import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal, PrimaryButton } from '../../components';
import ComponentToPrint from './ComponentToPrint';
import css from './Print.module.css';

const sanitizeFileNamePart = value =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const Print = props => {
  const intl = useIntl();
  const {
    providerName,
    orderId,
    startDate,
    address,
    buyerName,
    savedCart,
    onManageDisableScrolling,
  } = props;

  const pdfRef = useRef(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = async () => {
    if (!pdfRef.current || inProgress) {
      return;
    }

    setInProgress(true);
    setDownloadError(null);

    try {
      const jspdfLib = await import('jspdf');
      const JsPdfCtor = jspdfLib?.jsPDF;
      if (!JsPdfCtor) {
        throw new Error('jsPDF module is unavailable');
      }

      const content = pdfRef.current;
      const doc = new JsPdfCtor('p', 'mm');
      await new Promise((resolve, reject) => {
        try {
          doc.html(content, {
            callback: () => resolve(),
            html2canvas: { scale: 0.35 },
            margin: 14,
            fontFaces: [
              {
                family: 'Montserrat',
                style: 'normal',
              },
            ],
          });
        } catch (error) {
          reject(error);
        }
      });

      const safeOrderId = sanitizeFileNamePart(orderId);
      const fileName = safeOrderId ? `packing-slip-${safeOrderId}.pdf` : 'packing-slip.pdf';
      doc.save(fileName);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setDownloadError(intl.formatMessage({ id: 'TransactionPage.packingSlip.generateFailed' }));
    } finally {
      setInProgress(false);
    }
  };

  return (
    <>
      <PrimaryButton type="button" onClick={() => setModalOpen(true)}>
        <FormattedMessage id="TransactionPage.packingSlip.download" />
      </PrimaryButton>

      <Modal
        id="packingSlipModal"
        containerClassName={css.modalContainer}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <header className={css.pdfContainer}>
          {/* The preview scrolls; the PDF is made from the whole slip inside it, so orders with
              many items aren't cut off at the preview's height */}
          <div className={css.data}>
            <div ref={pdfRef}>
              <ComponentToPrint
                providerName={providerName}
                orderId={orderId}
                startDate={startDate}
                address={address}
                buyerName={buyerName}
                savedCart={savedCart}
              />
            </div>
          </div>
          {downloadError ? <p className={css.actionError}>{downloadError}</p> : null}
          <PrimaryButton
            type="button"
            onClick={handleDownload}
            inProgress={inProgress}
            rootClassName={css.downloadButton}
          >
            <FormattedMessage id="TransactionPage.packingSlip.download" />
          </PrimaryButton>
        </header>
      </Modal>
    </>
  );
};

export default Print;
